import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'
import { EnrichedIssueEntity } from '../../../types/IssueEntity'
import { FactsTableRow } from '../../components/FactsTable'
import { formatPercentage } from '../../../packages/utils/numberFormatters'
import { calculateDilution } from '../utils'

type DilutionRowComponent = React.FC<{
    issue: EnrichedIssueEntity
}>

type IssueDilutionRowComponent = React.FC<{
    issue: EnrichedIssueEntity
    locale: string
    t: (key: string, options?: Record<string, unknown>) => string
}>

const RightsIssueDiltionRow: IssueDilutionRowComponent = ({ issue, locale, t }) => {
    const { maxDilution, actualDilution } = calculateDilution(issue)

    if (!maxDilution) {
        return null
    }

    return (
        <FactsTableRow label={t('pages.issues.terms.dilution')}>
            <Typography>
                {t('pages.issues.terms.dilutionMax', {
                    value: formatPercentage(maxDilution, { locale, decimals: 0 }),
                })}
            </Typography>
            {actualDilution ? (
                <Typography>
                    {t('pages.issues.terms.dilutionActual', {
                        value: formatPercentage(actualDilution, { locale, decimals: 0 }),
                    })}
                </Typography>
            ) : null}
        </FactsTableRow>
    )
}

const OtherIssueDilutionRow: IssueDilutionRowComponent = ({ issue, locale, t }) => {
    const { totalOfferedUnits, unitSubTotal, numberOfSharesBeforePublicationDate } = issue

    const numberOfNewShares = unitSubTotal !== null ? unitSubTotal : totalOfferedUnits

    const dilution = numberOfNewShares
        ? (numberOfNewShares * 100) / (numberOfNewShares + (numberOfSharesBeforePublicationDate || 0))
        : null

    return (
        <FactsTableRow label={t('pages.issues.terms.dilution')}>
            <Typography>
                {dilution
                    ? formatPercentage(dilution, { locale, decimals: 0 })
                    : t('pages.issues.terms.newSharesMissing')}
            </Typography>
        </FactsTableRow>
    )
}

export const DilutionRow: DilutionRowComponent = ({ issue }) => {
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    if (issue.type === 'Rights issue') {
        return <RightsIssueDiltionRow issue={issue} locale={locale} t={t} />
    }

    return <OtherIssueDilutionRow issue={issue} locale={locale} t={t} />
}
